import React, {useEffect} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {ThemeProvider} from '@rneui/themed';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';
import {runInAction} from 'mobx';

import {rootStore} from '../../stores/rootStore';
import rewardsTheme from '../../theme/rewardsTheme';
import {ExtraPrice, OrderLine, OrderLineSticker} from '../../transport/apiServiceResponse';
import Text from '../ui-controls/Text';

import ProductTileActions from './primitives/ProductTileActions';
import ProductTileContainer from './primitives/ProductTileContainer';
import ProductTileImage from './primitives/ProductTileImage';
import ProductTileInfo from './primitives/ProductTileInfo';
import ProductTilePricing from './primitives/ProductTilePricing';
import ProductTileCarousel from './variants/ProductTileCarousel';
import ProductTileCompact from './variants/ProductTileCompact';

// =============================================================================
// MOCK DATA HELPERS
// =============================================================================

const createSticker = (overrides: Partial<OrderLineSticker> & {id: string}): OrderLineSticker =>
  ({
    type: 'stickers',
    labels: [],
    deal: false,
    saving: false,
    special: false,
    tag: false,
    sticker_type: 'saving',
    position: {horizontal: 'right', vertical: 'top'},
    ...overrides,
  }) as OrderLineSticker;

const createMockProduct = (overrides: Partial<OrderLine> = {}): OrderLine =>
  ({
    id: '123',
    product_id: 'prod_123',
    name: "Jacob's Creek Reserve Barossa Shiraz 750mL",
    sell_price_cents: 2199,
    normal_price_cents: 2799,
    size: {quantity: 1, size: '750', unit: 'mL'},
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=Wine',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=Wine',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=Wine',
    },
    stickers: [],
    extra_prices: [],
    ...overrides,
  }) as OrderLine;

// =============================================================================
// STICKER FIXTURES
// =============================================================================

const savingSticker = createSticker({
  id: 'sticker-save-3',
  labels: ['SAVE', '$3.00'],
  deal: false,
  saving: true,
  sticker_type: 'saving',
  position: {horizontal: 'right', vertical: 'top'},
});

const dealSticker = createSticker({
  id: 'sticker-2for60',
  labels: ['2 FOR', '$60'],
  deal: true,
  saving: false,
  sticker_type: 'saving',
  position: {horizontal: 'right', vertical: 'top'},
});

const tagStickerBottomLeft = createSticker({
  id: 'sticker-organic',
  labels: ['Balanced Lifestyle - Organic'],
  deal: false,
  saving: false,
  tag: true,
  sticker_type: 'tag' as OrderLineSticker['sticker_type'],
  position: {horizontal: 'left', vertical: 'bottom'},
  links: {image: 'https://placehold.co/80x80/4CAF50/fff?text=Organic'},
} as Partial<OrderLineSticker> & {id: string});

const tagStickerTopLeft = createSticker({
  id: 'sticker-lowcarb',
  labels: ['Low Carb'],
  deal: false,
  saving: false,
  tag: true,
  sticker_type: 'tag' as OrderLineSticker['sticker_type'],
  position: {horizontal: 'left', vertical: 'top'},
  links: {image: 'https://placehold.co/80x80/2196F3/fff?text=LowCarb'},
} as Partial<OrderLineSticker> & {id: string});

// =============================================================================
// EXTRA PRICE FIXTURES (for members banner)
// =============================================================================

const publicLoyaltyPrice: ExtraPrice = {
  normal_price: 2799,
  sell_price: 2199,
  external_sell_type: 'public',
  price_level_external_reference: 'lmg_loyalty',
};

const privateLoyaltyPrice: ExtraPrice = {
  normal_price: 2799,
  sell_price: 1999,
  external_sell_type: 'private',
  price_level_external_reference: 'lmg_loyalty',
};

const nonMemberPrice: ExtraPrice = {
  normal_price: 2799,
  sell_price: 2799,
  external_sell_type: 'public',
  price_level_external_reference: 'non_member_price',
};

// =============================================================================
// MOCK PRODUCTS
// =============================================================================

/** Basic products (no stickers, no member pricing) */
const basicProducts = [
  createMockProduct({
    product_id: 'prod_basic_1',
    name: 'Penfolds Bin 389 Cabernet Shiraz',
    sell_price_cents: 7499,
    normal_price_cents: 8999,
  }),
  createMockProduct({
    product_id: 'prod_basic_2',
    name: 'Corona Extra 24x355mL Bottles',
    sell_price_cents: 5999,
    size: {quantity: 24, size: 355, unit: 'mL'},
  }),
  createMockProduct({
    product_id: 'prod_basic_3',
    name: 'Bundaberg Rum UP 700mL',
    sell_price_cents: 4899,
    normal_price_cents: 5299,
  }),
];

/** Products with sticker variations */
const stickerProducts = [
  createMockProduct({
    product_id: 'prod_sticker_save',
    name: 'Farm Hand Organic Cabernet Sauvignon 750mL',
    sell_price_cents: 1299,
    normal_price_cents: 1599,
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=Red+Wine',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=Red+Wine',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=Red+Wine',
    },
    stickers: [savingSticker],
  }),
  createMockProduct({
    product_id: 'prod_sticker_deal',
    name: 'Corona Extra 24x355mL Bottles',
    sell_price_cents: 5999,
    size: {quantity: 24, size: 355, unit: 'mL'},
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=Beer',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=Beer',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=Beer',
    },
    stickers: [dealSticker],
  }),
  createMockProduct({
    product_id: 'prod_sticker_tag',
    name: 'Angove Organic Shiraz Cabernet 750mL',
    sell_price_cents: 1099,
    normal_price_cents: 1099,
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=Organic',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=Organic',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=Organic',
    },
    stickers: [tagStickerBottomLeft],
  }),
  createMockProduct({
    product_id: 'prod_sticker_multi',
    name: 'Bundaberg Up & Zero Can 10x375mL',
    sell_price_cents: 4599,
    normal_price_cents: 4899,
    size: {quantity: 10, size: 375, unit: 'mL'},
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=Cans',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=Cans',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=Cans',
    },
    stickers: [savingSticker, tagStickerBottomLeft],
  }),
  createMockProduct({
    product_id: 'prod_sticker_topleft',
    name: 'Hahn SuperDry 3.5 Low Carb 24x330mL',
    sell_price_cents: 4999,
    normal_price_cents: 4999,
    size: {quantity: 24, size: 330, unit: 'mL'},
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=LowCarb',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=LowCarb',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=LowCarb',
    },
    stickers: [tagStickerTopLeft],
  }),
];

/** Products with member pricing (requires loyalty-enabled store) */
const memberProducts = [
  createMockProduct({
    product_id: 'prod_member_public',
    name: 'Great Northern Original 24x330mL',
    sell_price_cents: 5499,
    normal_price_cents: 5999,
    size: {quantity: 24, size: 330, unit: 'mL'},
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=Beer',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=Beer',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=Beer',
    },
    extra_prices: [publicLoyaltyPrice, nonMemberPrice],
  }),
  createMockProduct({
    product_id: 'prod_member_private',
    name: 'Moet & Chandon Imperial Brut NV 750mL',
    sell_price_cents: 5999,
    normal_price_cents: 6999,
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=Champagne',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=Champagne',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=Champagne',
    },
    extra_prices: [privateLoyaltyPrice, nonMemberPrice],
  }),
  createMockProduct({
    product_id: 'prod_member_deal',
    name: 'VB 24x375mL Cans',
    sell_price_cents: 4999,
    normal_price_cents: 5599,
    size: {quantity: 24, size: 375, unit: 'mL'},
    links: {
      image: 'https://placehold.co/200x200/f5f5f5/666?text=VB',
      medium_image: 'https://placehold.co/200x200/f5f5f5/666?text=VB',
      large_image: 'https://placehold.co/400x400/f5f5f5/666?text=VB',
    },
    stickers: [dealSticker],
    extra_prices: [publicLoyaltyPrice, nonMemberPrice],
  }),
];

// =============================================================================
// DECORATORS
// =============================================================================

const withTheme = (Story: React.FC) => (
  <ThemeProvider theme={rewardsTheme}>
    <ScrollView style={styles.container}>
      <Story />
    </ScrollView>
  </ThemeProvider>
);

/**
 * Enables loyalty pricing on the singleton rootStore so MembersPriceLabel renders.
 * Restores original state on unmount.
 */
const withLoyaltyEnabled = (Story: React.FC) => {
  const EnableLoyalty = () => {
    useEffect(() => {
      runInAction(() => {
        (rootStore.shopSelectionStore as any)._loyaltyEnabled = true;
      });
      return () => {
        runInAction(() => {
          (rootStore.shopSelectionStore as any)._loyaltyEnabled = false;
        });
      };
    }, []);
    return <Story />;
  };
  return <EnableLoyalty />;
};

// =============================================================================
// META
// =============================================================================

const variantsMeta = {
  title: 'Components/Product/Product Tile',
  decorators: [withTheme],
} satisfies Meta;

export default variantsMeta;

type VariantsStory = StoryObj<typeof variantsMeta>;

// =============================================================================
// BASIC VARIANT STORIES
// =============================================================================

export const CompactVariant: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>ProductTileCompact</Text>
      <Text style={styles.description}>
        Vertical layout for grid displays. Shows image, name, byline, pricing, and cart button.
      </Text>
      <View style={styles.tileRow}>
        <ProductTileCompact
          item={basicProducts[0]}
          onPress={item => console.log('Pressed:', item.name)}
          testContext="story"
        />
        <ProductTileCompact
          item={basicProducts[1]}
          onPress={item => console.log('Pressed:', item.name)}
          testContext="story"
        />
      </View>
    </View>
  ),
};

export const CarouselVariant: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>ProductTileCarousel</Text>
      <Text style={styles.description}>
        Horizontal carousel layout with shadow styling. Optimized for product carousels.
      </Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {basicProducts.map(product => (
          <ProductTileCarousel
            key={product.product_id}
            item={product}
            onPress={item => console.log('Pressed:', item.name)}
            testContext="story"
          />
        ))}
      </ScrollView>
    </View>
  ),
};

export const AllVariantsComparison: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Variant Comparison</Text>
      <Text style={styles.description}>Side-by-side comparison of all ProductTile variants.</Text>

      <Text style={styles.subTitle}>Compact (for grids)</Text>
      <View style={styles.tileRow}>
        <ProductTileCompact item={basicProducts[0]} testContext="story" />
      </View>

      <Text style={styles.subTitle}>Carousel (for horizontal scrolling)</Text>
      <View style={styles.tileRow}>
        <ProductTileCarousel item={basicProducts[0]} testContext="story" />
      </View>
    </View>
  ),
};

// =============================================================================
// STICKER STORIES
// =============================================================================

export const WithStickers: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Sticker Variations</Text>
      <Text style={styles.description}>
        Product tiles with different sticker types: saving badges, deal badges, image tags, and
        multi-sticker combinations.
      </Text>

      <Text style={styles.subTitle}>Carousel with Stickers</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {stickerProducts.map(product => (
          <ProductTileCarousel key={product.product_id} item={product} testContext="story" />
        ))}
      </ScrollView>

      <Text style={styles.subTitle}>Compact Grid with Stickers</Text>
      <View style={styles.tileRow}>
        {stickerProducts.map(product => (
          <ProductTileCompact key={product.product_id} item={product} testContext="story" />
        ))}
      </View>
    </View>
  ),
};

// =============================================================================
// MEMBERS BANNER STORIES
// =============================================================================

export const WithMembersBanner: VariantsStory = {
  decorators: [withLoyaltyEnabled],
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Members Price Banner</Text>
      <Text style={styles.description}>
        Top banner variations: "MEMBERS PRICE" (public loyalty), "JUST FOR YOU" (private loyalty),
        and combined with deal stickers. Requires loyalty-enabled store.
      </Text>

      <Text style={styles.subTitle}>Carousel</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {memberProducts.map(product => (
          <ProductTileCarousel key={product.product_id} item={product} testContext="story" />
        ))}
        {/* Include a non-member product for comparison */}
        <ProductTileCarousel item={basicProducts[0]} testContext="story" />
      </ScrollView>

      <Text style={styles.subTitle}>Compact Grid</Text>
      <View style={styles.tileRow}>
        {memberProducts.map(product => (
          <ProductTileCompact key={product.product_id} item={product} testContext="story" />
        ))}
        <ProductTileCompact item={basicProducts[0]} testContext="story" />
      </View>
    </View>
  ),
};

// =============================================================================
// FULL SHOWCASE
// =============================================================================

export const FullShowcase: VariantsStory = {
  decorators: [withLoyaltyEnabled],
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Full Showcase</Text>
      <Text style={styles.description}>
        Complete visual QA: all product variations (basic, stickers, member pricing) in both tile
        variants. Loyalty enabled for banner rendering.
      </Text>

      <Text style={styles.subTitle}>Carousel — All Variations</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {[...basicProducts.slice(0, 1), ...stickerProducts, ...memberProducts].map(product => (
          <ProductTileCarousel key={product.product_id} item={product} testContext="story" />
        ))}
      </ScrollView>

      <Text style={styles.subTitle}>Compact Grid — All Variations</Text>
      <View style={styles.tileRow}>
        {[...basicProducts.slice(0, 1), ...stickerProducts, ...memberProducts].map(product => (
          <ProductTileCompact key={product.product_id} item={product} testContext="story" />
        ))}
      </View>
    </View>
  ),
};

// =============================================================================
// PRIMITIVE STORIES
// =============================================================================

export const ImagePrimitive: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>ProductTileImage</Text>
      <Text style={styles.description}>Image display with size options and sticker support.</Text>

      <View style={styles.primitiveRow}>
        <View style={styles.primitiveItem}>
          <Text style={styles.label}>Small</Text>
          <ProductTileImage item={basicProducts[0]} size="small" />
        </View>
        <View style={styles.primitiveItem}>
          <Text style={styles.label}>Medium</Text>
          <ProductTileImage item={basicProducts[0]} size="medium" />
        </View>
        <View style={styles.primitiveItem}>
          <Text style={styles.label}>Large</Text>
          <ProductTileImage item={basicProducts[0]} size="large" />
        </View>
      </View>
    </View>
  ),
};

export const InfoPrimitive: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>ProductTileInfo</Text>
      <Text style={styles.description}>Product name and byline display.</Text>

      <View style={[styles.primitiveItem, {width: 200}]}>
        <ProductTileInfo item={basicProducts[0]} nameNumberOfLines={2} />
      </View>

      <Text style={styles.label}>With 1 line limit:</Text>
      <View style={[styles.primitiveItem, {width: 200}]}>
        <ProductTileInfo item={basicProducts[0]} nameNumberOfLines={1} />
      </View>
    </View>
  ),
};

export const PricingPrimitive: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>ProductTilePricing</Text>
      <Text style={styles.description}>Price display with member labels and original price.</Text>

      <View style={styles.primitiveItem}>
        <Text style={styles.label}>Sale Price (with original)</Text>
        <ProductTilePricing item={basicProducts[0]} showMemberLabel showOriginalPrice />
      </View>

      <View style={styles.primitiveItem}>
        <Text style={styles.label}>Regular Price</Text>
        <ProductTilePricing item={basicProducts[1]} showMemberLabel />
      </View>
    </View>
  ),
};

export const ActionsPrimitive: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>ProductTileActions</Text>
      <Text style={styles.description}>Cart and wishlist action buttons.</Text>

      <View style={[styles.primitiveItem, {width: 150}]}>
        <ProductTileActions item={basicProducts[0]} testContext="story" />
      </View>
    </View>
  ),
};

export const ContainerPrimitive: VariantsStory = {
  render: () => (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>ProductTileContainer</Text>
      <Text style={styles.description}>Touchable wrapper with press handling.</Text>

      <ProductTileContainer
        item={basicProducts[0]}
        onPress={item => console.log('Container pressed:', item.name)}
        testID="story-container"
        containerStyle={styles.demoContainer}>
        <View style={styles.containerContent}>
          <Text>Tap me!</Text>
          <Text style={styles.smallText}>This is a touchable container</Text>
        </View>
      </ProductTileContainer>
    </View>
  ),
};

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#f5f5f5',
  },
  section: {
    marginBottom: 32,
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 8,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  subTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    color: '#666',
    marginBottom: 16,
  },
  tileRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  primitiveRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-end',
  },
  primitiveItem: {
    marginBottom: 16,
  },
  label: {
    fontSize: 12,
    color: '#999',
    marginBottom: 4,
  },
  smallText: {
    fontSize: 12,
    color: '#666',
  },
  demoContainer: {
    padding: 16,
    width: 150,
  },
  containerContent: {
    alignItems: 'center',
  },
});
